// client/src/components/pages/LexiStar/RouteTruckDriver/RankedBars.tsx
//
// The ranked bars of "Routes, trucks & drivers" (ST-00254 / 255 / 256): one horizontal bar per
// route, truck or driver for the ranked column, highest first, with the comparison figure (the
// allowance, the plan) as a thinner muted bar under it. Nominal categories take the one accent (dataviz:
// never colour nominal bars by value); a selected row keeps the accent while the others step
// back. A click on a bar selects its row (the page then shows that row's trend and, for
// speeding, the episodes behind it). Colours, grid and tooltip resolve through the theme's
// variables (ST-00243) so the chart reads on the light and the dark surface. Recharts children
// are passed as arrays, never inside a fragment (ST-00260).

import { Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { formatCell, formatTick, type RtdColumn, type RtdRow } from './rtdLogic';

const tw = (name: string) => `rgb(var(--tw-${name}))`;
const PRIMARY = tw('primary-500');
const PRIMARY_MUTED = tw('primary-200');
const COMPARE = tw('slate-300');
const GRID = tw('slate-200');
const TICK = { fontSize: 11, fill: tw('slate-600') };
const TOOLTIP_STYLE = {
  background: 'var(--surface-raised)',
  border: '1px solid var(--border)',
  borderRadius: 8,
  color: 'var(--text)',
  fontSize: 12,
};
const TOOLTIP_LABEL = { color: 'var(--text-strong)', fontWeight: 600 };

const ROW_H = 30;
const ROW_H_PAIR = 40;
const MARGIN = { top: 4, right: 16, bottom: 0, left: 4 };

export interface RankedBarsProps {
  rows: RtdRow[];
  column: RtdColumn;
  compare: RtdColumn | null;
  selected: string | null;
  onSelect: (id: string | null) => void;
  /** Fixed label width (the longest label clipped with an ellipsis by the axis). */
  labelWidth?: number;
}

interface Datum {
  id: string;
  label: string;
  primary: number | null;
  compare: number | null;
}

export default function RankedBars({ rows, column, compare, selected, onSelect, labelWidth = 200 }: RankedBarsProps) {
  const data: Datum[] = rows.map((r) => ({
    id: r.id,
    label: r.label,
    primary: r.values[column.key] ?? null,
    compare: compare ? r.values[compare.key] ?? null : null,
  }));
  const rowH = compare ? ROW_H_PAIR : ROW_H;
  const height = Math.max(rowH * 2, rowH * data.length + MARGIN.top + 24);
  const diverging = column.diverging && data.some((d) => (d.primary ?? 0) < 0);
  const summary = data.length
    ? `${column.label}, highest first: ${data
        .slice(0, 5)
        .map((d) => `${d.label} ${formatCell(column, d.primary)}`)
        .join('; ')}${data.length > 5 ? `; and ${data.length - 5} more` : ''}.`
    : `No ${column.label.toLowerCase()} recorded.`;
  const bars = [
    <Bar
      key="primary"
      dataKey="primary"
      name={column.label}
      fill={PRIMARY}
      maxBarSize={14}
      radius={[0, 3, 3, 0]}
      isAnimationActive={false}
      cursor="pointer"
      onClick={(d: { payload?: Datum }) => {
        const id = d?.payload?.id;
        if (id) onSelect(selected === id ? null : id);
      }}
    >
      {data.map((d) => (
        <Cell key={d.id} fill={selected && selected !== d.id ? PRIMARY_MUTED : PRIMARY} />
      ))}
    </Bar>,
    ...(compare
      ? [
          <Bar
            key="compare"
            dataKey="compare"
            name={compare.label}
            fill={COMPARE}
            maxBarSize={8}
            radius={[0, 3, 3, 0]}
            isAnimationActive={false}
          />,
        ]
      : []),
  ];
  return (
    <figure className="m-0" data-testid="ranked-bars" data-rows={data.length} aria-label={summary}>
      <div style={{ width: '100%', height }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} layout="vertical" margin={MARGIN} barGap={2} barCategoryGap={compare ? "22%" : "30%"}>
            {[
              <CartesianGrid key="g" horizontal={false} stroke={GRID} strokeDasharray="3 3" />,
              <XAxis
                key="x"
                type="number"
                tick={TICK}
                tickLine={false}
                axisLine={false}
                tickFormatter={(v: number) => formatTick(column, v)}
                domain={diverging ? ['auto', 'auto'] : [0, 'auto']}
              />,
              <YAxis
                key="y"
                type="category"
                dataKey="label"
                width={labelWidth}
                tick={TICK}
                tickLine={false}
                axisLine={false}
                interval={0}
              />,
              <Tooltip
                key="t"
                isAnimationActive={false}
                cursor={{ fill: 'rgb(var(--tw-slate-100))' }}
                contentStyle={TOOLTIP_STYLE}
                labelStyle={TOOLTIP_LABEL}
                formatter={(v: unknown, name: string) => [
                  formatCell(name === column.label ? column : compare || column, typeof v === 'number' ? v : null),
                  name,
                ]}
              />,
              ...bars,
            ]}
          </BarChart>
        </ResponsiveContainer>
      </div>
      <figcaption className="sr-only">{summary}</figcaption>
    </figure>
  );
}
